import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from "@mui/material";

const SkillMatchingDialog = ({ open, onClose, candidate, job }) => {
  if (!open || !candidate) return null;

  const breakdown = candidate.breakdown || {};
  const scores = candidate.scores || {};
  const technical = breakdown.technical || {};
  const soft = breakdown.soft || {};
  const environment = breakdown.environment || {};
  const language = breakdown.language || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "#6c5ce7",
          color: "white",
        }}
      >
        Detailed Skill Matching Analysis
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
        dividers
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Job Role: {job?.job_role_name}
          </Typography>

          <Box sx={{ mb: 3 }}>
            {/* Candidate Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Candidate:{" "}
                {candidate.candidate?.name ||
                  candidate.candidate?.email_masked ||
                  "Unknown"}
              </Typography>
              <Chip
                label={scores.label || "Unknown"}
                size="small"
                sx={{
                  backgroundColor:
                    scores.gate_fail || scores.label === "Not a Fit"
                      ? "#ba68c8"
                      : scores.label === "Strong Match"
                        ? "#9b42e9" // Success/Strong
                        : "#8c5eb4",
                  color: "#FFFFFF",
                }}
              />
            </Stack>

            {/* Scores */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Technical Score:</strong> {scores.technical_score}
              </Typography>
              <Typography variant="body2">
                <strong>Soft Score:</strong> {scores.soft_score}
              </Typography>
              <Typography variant="body2">
                <strong>Environment Score:</strong> {scores.environment_score}
              </Typography>
              <Typography variant="body2">
                <strong>Overall Score:</strong> {scores.overall_score}
              </Typography>
            </Box>

            {/* Technical Skills Breakdown */}
            {technical.categories?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#9b42e9", fontWeight: "bold", mb: 1 }}
                >
                  Technical Skills
                </Typography>
                {technical.categories.map((cat, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #eee",
                      borderRadius: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {cat.category}
                      </Typography>
                      <Chip
                        label={cat.priority}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          color: "#9b42e9",
                          borderColor: "#9b42e9",
                          fontWeight: "bold",
                        }}
                      />
                    </Stack>

                    {/* Strengths */}
                    {cat.strengths?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: "bold",
                            color: "#9b42e9", // Purple
                            textTransform: "uppercase",
                          }}
                        >
                          Strengths
                        </Typography>
                        <List dense disablePadding>
                          {cat.strengths.map((strength, sIdx) => (
                            <ListItem
                              key={sIdx}
                              disablePadding
                              sx={{ py: 0.5 }}
                            >
                              <ListItemText
                                primaryTypographyProps={{
                                  variant: "body2",
                                  sx: !strength.subSkill
                                    ? { fontWeight: "bold" }
                                    : {},
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                }}
                                primary={
                                  strength.subSkill
                                    ? strength.subSkill
                                    : "Skill Proficiency"
                                }
                                secondary={`Required: ${strength.category_level ||
                                  strength.required_level ||
                                  "-"
                                  }, Candidate: ${strength.candidate_level || "-"
                                  }`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Gaps */}
                    {cat.gaps?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: "bold",
                            color: "#ba68c8",
                            textTransform: "uppercase",
                          }}
                        >
                          Gaps
                        </Typography>
                        <List dense disablePadding>
                          {cat.gaps.map((gap, gIdx) => (
                            <ListItem
                              key={gIdx}
                              disablePadding
                              sx={{ py: 0.5 }}
                            >
                              <ListItemText
                                primaryTypographyProps={{
                                  variant: "body2",
                                  sx: !gap.subSkill
                                    ? { fontWeight: "bold" }
                                    : {},
                                }}
                                secondaryTypographyProps={{
                                  variant: "caption",
                                }}
                                primary={
                                  gap.subSkill
                                    ? gap.subSkill
                                    : "Skill Proficiency"
                                }
                                secondary={`Required: ${gap.category_level ||
                                  gap.required_level ||
                                  "-"
                                  }, Candidate: ${gap.candidate_level || "Missing"
                                  }`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    {/* No strengths or gaps — show the category label */}
                    {!cat.strengths?.length &&
                      !cat.gaps?.length &&
                      cat.label && (
                        <Box
                          sx={{
                            mt: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={cat.label}
                            size="small"
                            sx={{
                              backgroundColor: "#f3e5f5",
                              color: "#7b1fa2",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        </Box>
                      )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Soft Skills Breakdown */}
            {(soft.matched?.length > 0 || soft.missing?.length > 0) && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#9b42e9", fontWeight: "bold", mb: 1 }}
                >
                  Soft Skills
                </Typography>

                {soft.matched?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "#9b42e9" }}
                    >
                      Match:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ gap: 1 }}
                    >
                      {soft.matched.map((s, i) => (
                        <Chip
                          key={i}
                          label={s}
                          // color="success" // Removed success color to use custom purple border/text if needed? Or keep green to indicate match?
                          // User requested "all label... purple shaded".
                          // Let's use purple outline for consistency with request.
                          variant="outlined"
                          size="small"
                          sx={{
                            color: "#9b42e9",
                            borderColor: "#9b42e9",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {soft.missing?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "#ba68c8" }}
                    >
                      Missing:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ gap: 1 }}
                    >
                      {soft.missing.map((s, i) => (
                        <Chip
                          key={i}
                          label={s}
                          // color="error" // Removed error color
                          variant="outlined"
                          size="small"
                          sx={{
                            color: "#ba68c8",
                            borderColor: "#ba68c8",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {/* Environment Breakdown */}
            {environment && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#9b42e9", fontWeight: "bold", mb: 1 }}
                >
                  Environment Fit
                </Typography>
                <List dense>
                  {environment.overlapping_preferred_company_types?.length >
                    0 && (
                      <ListItem>
                        <ListItemText
                          primaryTypographyProps={{
                            sx: { color: "#9b42e9", fontWeight: "bold" },
                          }}
                          primary="Matching Preferred Company Type by Equally Expert"
                          secondary={
                            environment.overlapping_preferred_company_types
                              ?.length > 0
                              ? environment.overlapping_preferred_company_types.join(
                                ", ",
                              )
                              : "No matching preferred company type found"
                          }
                        />
                      </ListItem>
                    )}
                  {environment.overlapping_company_types?.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primaryTypographyProps={{
                          sx: { color: "#9b42e9", fontWeight: "bold" },
                        }}
                        primary="Matching Company Types With Candidate"
                        secondary={
                          environment.overlapping_company_types?.length > 0
                            ? environment.overlapping_company_types.join(", ")
                            : "No matching company types found"
                        }
                      />
                    </ListItem>
                  )}
                  {environment.overlapping_preferred_work_environments && (
                    <ListItem>
                      <ListItemText
                        primaryTypographyProps={{
                          sx: { color: "#9b42e9", fontWeight: "bold" },
                        }}
                        primary="Matching Preferred Work Environment by Equally Expert"
                        secondary={
                          environment.overlapping_preferred_work_environments
                            ?.length > 0
                            ? environment.overlapping_preferred_work_environments.join(
                              ", ",
                            )
                            : "No matching preferred work environment found"
                        }
                      />
                    </ListItem>
                  )}
                  {environment.overlapping_work_environments?.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primaryTypographyProps={{
                          sx: { color: "#9b42e9", fontWeight: "bold" },
                        }}
                        primary="Matching Work Environments With Candidate"
                        secondary={
                          environment.overlapping_work_environments?.length > 0
                            ? environment.overlapping_work_environments.join(
                              ", ",
                            )
                            : "No matching work environments found"
                        }
                      />
                    </ListItem>
                  )}
                </List>
                {!environment.overlapping_company_types?.length &&
                  !environment.overlapping_work_environments?.length &&
                  !environment.overlapping_preferred_company_types?.length &&
                  !environment.overlapping_preferred_work_environments
                    ?.length && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 1,
                        fontStyle: "italic",
                      }}
                    >
                      No environment fit found.
                    </Typography>
                  )}
              </Box>
            )}

            {/* Language Requirements Breakdown */}
            {language && language.failures?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#d32f2f", fontWeight: "bold", mb: 1 }}
                >
                  Language Requirements
                </Typography>
                <Chip
                  label="Language Requirement Failed"
                  size="small"
                  sx={{
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    mb: 1.5,
                  }}
                />
                <List dense disablePadding>
                  {language.failures.map((failure, idx) => (
                    <ListItem
                      key={idx}
                      disablePadding
                      sx={{
                        py: 0.5,
                        px: 1,
                        mb: 0.5,
                        border: "1px solid #ffcdd2",
                        borderRadius: 1,
                        backgroundColor: "#fff8f8",
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: { fontWeight: "bold", textTransform: "capitalize" },
                        }}
                        secondaryTypographyProps={{ variant: "caption" }}
                        primary={failure.language}
                        secondary={`Required Level: ${failure.required_level} — Candidate Level: ${failure.candidate_level}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkillMatchingDialog;
